import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import {
  createUserSequencingDeck,
  updateUserSequencingDeck,
  getUserSequencingDeckById,
  uploadSequencingStepImage,
} from '../services/firestoreService';
import { CharCounter } from '../components/forms/CharCounter';
import { sanitizeInput, CHAR_LIMITS } from '../utils/sanitize';
import { validateImage } from '../utils/validateImage';
import { logAuditEvent, AUDIT_EVENTS } from '../services/auditService';
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiUpload, FiCamera, FiSearch } from 'react-icons/fi';
import '../styles/deck-builder.css';
import '../styles/sequencing-builder.css';

const PEXELS_KEY = process.env.REACT_APP_PEXELS_API_KEY;

// Pre-paired emoji + background color for the activity's icon
const EMOJI_PRESETS = [
  { emoji: '🧩', color: '#FFF0F3' },
  { emoji: '🪥', color: '#D6EAFA' },
  { emoji: '🧼', color: '#D6F5DC' },
  { emoji: '👕', color: '#EDD6FA' },
  { emoji: '🍽️', color: '#F2E4D0' },
  { emoji: '🎒', color: '#FAD6EC' },
  { emoji: '🛏️', color: '#F5F3FF' },
  { emoji: '🚿', color: '#CCF0EC' },
  { emoji: '🧸', color: '#FFF3CC' },
  { emoji: '🌟', color: '#FFFBEB' },
  { emoji: '🏠', color: '#FAE4D0' },
  { emoji: '📋', color: '#D0D9F5' },
];

let stepUid = 0;
const newStep = () => ({
  uid: `step_${Date.now()}_${stepUid++}`,
  label: '',
  imageUrl: null,
  imageFile: null,
  imageSource: null, // 'web' | 'uploaded'
});

// ── Per-step image picker (search web / upload / camera) ─────────────────────
const StepImagePicker = ({ step, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const runSearch = async (term) => {
    const q = (term ?? query ?? step.label).trim();
    if (!q) return;
    setSearching(true);
    setResults([]);
    try {
      const res = await fetch(
        `https://api.pexels.com/v1/search?query=${encodeURIComponent(q)}&per_page=12&orientation=square`,
        { headers: { Authorization: PEXELS_KEY } }
      );
      const data = await res.json();
      setResults(data.photos || []);
    } catch {
      setResults([]);
    }
    setSearching(false);
  };

  const pickPhoto = (photo) => {
    onChange({ ...step, imageUrl: photo.src.medium, imageFile: null, imageSource: 'web' });
    setResults([]);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setError('');
    const result = await validateImage(file);
    if (!result.valid) { setError(result.error); return; }
    if (step.imageSource === 'uploaded' && step.imageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(step.imageUrl);
    }
    onChange({ ...step, imageUrl: URL.createObjectURL(file), imageFile: file, imageSource: 'uploaded' });
  };

  const removeImage = () => {
    if (step.imageSource === 'uploaded' && step.imageUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(step.imageUrl);
    }
    onChange({ ...step, imageUrl: null, imageFile: null, imageSource: null });
  };

  if (step.imageUrl) {
    return (
      <div className="seqb-step-preview">
        <img src={step.imageUrl} alt="" className="seqb-step-preview-img" />
        <button type="button" className="seqb-step-preview-remove" onClick={removeImage}>
          ✕ Remove photo
        </button>
      </div>
    );
  }

  return (
    <div className="seqb-img-picker">
      <div className="db-img-source-row">
        <button
          type="button"
          className="db-img-source-btn active"
          onClick={() => { if (results.length === 0) runSearch(step.label); }}
        >
          <FiSearch size={13} /> Search Web
        </button>
        <button type="button" className="db-img-source-btn" onClick={() => fileInputRef.current?.click()}>
          <FiUpload size={13} /> Upload
        </button>
        <button type="button" className="db-img-source-btn" onClick={() => cameraInputRef.current?.click()}>
          <FiCamera size={13} /> Camera
        </button>
      </div>

      <div className="db-search-panel">
        <form className="db-search-input-row" onSubmit={e => { e.preventDefault(); runSearch(query); }}>
          <input
            className="seqb-input"
            type="text"
            placeholder={step.label.trim() ? `Search "${step.label.trim()}"…` : 'Type a search term'}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <button className="db-search-go-btn" type="submit" disabled={searching || (!query.trim() && !step.label.trim())}>
            {searching ? '…' : 'Search'}
          </button>
        </form>

        {results.length > 0 && (
          <div className="db-search-results-grid" style={{ marginTop: 8 }}>
            {results.map(photo => (
              <div key={photo.id} className="db-search-result-tile" onClick={() => pickPhoto(photo)}>
                <img
                  src={photo.src?.small}
                  alt=""
                  className="db-search-result-img"
                  onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                />
              </div>
            ))}
          </div>
        )}

        {!searching && results.length === 0 && (query || step.label) && (
          <p className="seqb-hint" style={{ marginTop: 8 }}>No results — try a different keyword, or upload/take a photo instead.</p>
        )}
      </div>

      {error && <p className="db-upload-error">{error}</p>}
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
};

export const SequencingBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const editDeckId = location.state?.editDeckId ?? null;

  const [title, setTitle] = useState('');
  const [preset, setPreset] = useState(EMOJI_PRESETS[0]);
  const [steps, setSteps] = useState([newStep(), newStep()]);

  const [editLoading, setEditLoading] = useState(!!editDeckId);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!editDeckId) return;
    getUserSequencingDeckById(editDeckId).then(result => {
      if (result.success) {
        const d = result.deck;
        setTitle(d.title || '');
        setPreset({ emoji: d.emoji || EMOJI_PRESETS[0].emoji, color: d.color || EMOJI_PRESETS[0].color });
        setSteps(
          (d.steps || []).length
            ? d.steps.map(s => ({ uid: s.id || newStep().uid, label: s.label || '', imageUrl: s.imageUrl || null, imageFile: null, imageSource: s.imageUrl ? 'web' : null }))
            : [newStep(), newStep()]
        );
      }
      setEditLoading(false);
    });
  }, [editDeckId]);

  const updateStep = (uid, next) => {
    setSteps(prev => prev.map(s => (s.uid === uid ? next : s)));
  };

  const removeStep = (uid) => {
    setSteps(prev => prev.filter(s => s.uid !== uid));
  };

  const moveStep = (index, dir) => {
    setSteps(prev => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addStep = () => setSteps(prev => [...prev, newStep()]);

  const canSave = title.trim().length > 0
    && steps.length >= 2
    && steps.every(s => s.label.trim().length > 0 && s.imageUrl);

  const handleSave = async () => {
    if (!canSave || saving || !currentUser) return;
    setSaving(true);
    setSaveError('');

    try {
      const payload = {
        title: sanitizeInput(title.trim()),
        emoji: preset.emoji,
        color: preset.color,
      };

      let deckId = editDeckId;
      if (!deckId) {
        const createRes = await createUserSequencingDeck(currentUser.uid, payload);
        if (!createRes.success) throw new Error(createRes.error || 'Could not create activity.');
        deckId = createRes.id;
      }

      const finalSteps = [];
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        let imageUrl = step.imageUrl;
        if (step.imageFile) {
          const uploadRes = await uploadSequencingStepImage(currentUser.uid, deckId, step.uid, step.imageFile);
          if (!uploadRes.success) throw new Error(uploadRes.error || 'Image upload failed.');
          imageUrl = uploadRes.url;
        }
        finalSteps.push({
          id: step.uid,
          order: i + 1,
          label: sanitizeInput(step.label.trim()),
          imageUrl,
          images: [imageUrl],
          bgColor: preset.color,
          emoji: preset.emoji,
        });
      }

      await updateUserSequencingDeck(deckId, { ...payload, steps: finalSteps, stepCount: finalSteps.length });

      logAuditEvent(currentUser.uid, AUDIT_EVENTS.DECK_CREATED, { type: 'sequencing', deckId });

      navigate('/sequencing', { state: { newDeckId: deckId } });
    } catch (err) {
      setSaveError(err.message || 'Something went wrong saving your activity.');
      setSaving(false);
    }
  };

  if (editLoading) {
    return (
      <Layout>
        <div className="seqb-page">
          <div className="seqb-loading">Loading your activity…</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="seqb-page">
        <div className="page-header">
          <h1 className="deck-page-title">{editDeckId ? 'Edit Sequencing Activity' : 'Create Sequencing Activity'}</h1>
          <p>Build your own step-by-step picture sequence using your own photos, the camera, or a web search.</p>
        </div>

        <div className="seqb-card">
          <div className="seqb-field">
            <label className="seqb-field-label">Activity Name *</label>
            <input
              className="db-input db-input-large"
              placeholder="e.g., Brushing Your Teeth"
              value={title}
              onChange={e => { if (e.target.value.length <= CHAR_LIMITS.deckTitle) setTitle(e.target.value); }}
              maxLength={CHAR_LIMITS.deckTitle}
            />
            <CharCounter current={title.length} max={CHAR_LIMITS.deckTitle} />
          </div>

          <div className="seqb-field">
            <label className="seqb-field-label">Icon</label>
            <div className="seqb-emoji-grid">
              {EMOJI_PRESETS.map(p => (
                <button
                  key={p.emoji}
                  type="button"
                  className={`seqb-emoji-swatch${preset.emoji === p.emoji ? ' selected' : ''}`}
                  style={{ background: p.color }}
                  onClick={() => setPreset(p)}
                >
                  {p.emoji}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="seqb-card">
          <div className="seqb-card-header">
            <h2 className="seqb-card-title">Steps</h2>
            <span className="seqb-step-count">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
          </div>

          {steps.map((step, i) => (
            <div key={step.uid} className="seqb-step-row">
              <div className="seqb-step-order-col">
                <span className="seqb-step-order-badge">{i + 1}</span>
                <button type="button" className="seqb-step-move-btn" disabled={i === 0} onClick={() => moveStep(i, -1)} title="Move up">
                  <FiChevronUp size={14} />
                </button>
                <button type="button" className="seqb-step-move-btn" disabled={i === steps.length - 1} onClick={() => moveStep(i, 1)} title="Move down">
                  <FiChevronDown size={14} />
                </button>
              </div>

              <div className="seqb-step-body">
                <div className="seqb-field">
                  <label className="seqb-field-label">Step {i + 1} label *</label>
                  <input
                    className="db-input"
                    placeholder="e.g., Turn on the water"
                    value={step.label}
                    onChange={e => {
                      if (e.target.value.length <= CHAR_LIMITS.cardLabel) {
                        updateStep(step.uid, { ...step, label: e.target.value });
                      }
                    }}
                    maxLength={CHAR_LIMITS.cardLabel}
                  />
                  <CharCounter current={step.label.length} max={CHAR_LIMITS.cardLabel} />
                </div>

                <StepImagePicker step={step} onChange={next => updateStep(step.uid, next)} />
              </div>

              <button
                type="button"
                className="seqb-step-remove-btn"
                onClick={() => removeStep(step.uid)}
                disabled={steps.length <= 2}
                title="Remove step"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          ))}

          <button type="button" className="seqb-add-step-btn" onClick={addStep}>
            <FiPlus size={15} /> Add Step
          </button>
        </div>

        {saveError && <p className="db-upload-error">{saveError}</p>}

        <div className="seqb-save-row">
          <button
            type="button"
            className="seqb-save-btn"
            disabled={!canSave || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : editDeckId ? 'Save Changes' : 'Save Activity'}
          </button>
          {!canSave && (
            <span className="seqb-hint">Give it a name, and at least 2 steps each with a label and a photo.</span>
          )}
        </div>
      </div>
    </Layout>
  );
};
